<template>
    <v-container fluid class="uv-flex-fill-remaining">
        <v-sheet style="height: 100%" class="rounded-xl pa-3 overflow-container">
            <v-row align-content="stretch">
                <v-col cols="12" md="2" style="position: relative;">
                    <v-avatar height="100%" width="100%" class="elevation-4">
                        <v-img v-if="!currentAvatarData.busy && userData.data.photo"
                            :src="currentAvatarData.image.src" aspect-ratio="1"></v-img>
                        <v-icon v-else>{{ icons.mdiAccount }}</v-icon>
                    </v-avatar>
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-file-input v-on="on" v-bind="attrs" class="edit-avatar" hide-details hide-input dense
                                @change="onImageLoad" accept="image/*" :value="avatarData.image"
                                :prepend-icon="icons.mdiPencil" color="warning">
                            </v-file-input>
                        </template>
                        {{ $t('labels.edit') }}
                    </v-tooltip>
                </v-col>
                <v-col cols="12" md="8" class="text-h6">
                    <div class="d-flex flex-column justify-space-around fill-height">
                        <div>
                            <span>{{ $t('labels.name') }}: </span>
                            <span>{{ userData.data.name }}</span>
                        </div>
                        <div>
                            <span>{{ $t('labels.email') }}: </span>
                            <span>{{ userData.data.email }}</span>
                        </div>
                        <div>
                            <span>{{ $t('labels.phone') }}: </span>
                            <span>{{ userData.data.phone }}</span>
                        </div>
                        <div>
                            <span>{{ $t('labels.username') }}: </span>
                            <span>{{ userData.data.username }}</span>
                        </div>
                    </div>
                </v-col>
                <v-col cols="12">
                    <v-divider></v-divider>
                </v-col>
                <v-col cols="12" md="6" style="min-height: 300px" self-align="stretch">
                    <v-card class="fill-height">
                        <validation-observer v-slot="{ handleSubmit }" ref="observer" class="fill-height">
                            <v-form @submit.prevent="handleSubmit(updateUserDetails())"
                                class="fill-height d-flex flex-column">
                                <v-card-title>
                                    <span>
                                        {{ $t('labels.userDetails') }}
                                    </span>
                                </v-card-title>
                                <v-card-text class="flex-grow-1">
                                    <v-row>
                                        <v-col cols="6">
                                            <uv-text-field :label="$t('labels.user')" v-model="userData.data.username"
                                                rules="required" color="info"></uv-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <uv-text-field :label="$t('labels.firstAndLastName')"
                                                v-model="userData.data.name" rules="required" color="info"></uv-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <uv-text-field :label="$t('labels.phone')" v-model="userData.data.phone"
                                                color="info" rules="phone">
                                                <template #prepend-inner>
                                                    <v-icon color="info" size="28">{{ icons.mdiPhone }}</v-icon>
                                                </template>
                                            </uv-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <uv-text-field :label="$t('labels.email')" v-model="userData.data.email"
                                                color="info" rules="required|email">
                                                <template #prepend-inner>
                                                    <v-icon color="info" size="28">$email</v-icon>
                                                </template>
                                            </uv-text-field>
                                        </v-col>
                                        <v-col cols="12"><span class="text-h6">{{ $t('labels.signature') }}</span></v-col>
                                        <v-col cols="12">
                                            <uv-editor ref="signature" v-if="!userData.busy" :initial-content="userData.data.text"
                                                :isEditable="true" :isEmailEditor="true"
                                                class="flex-grow-1 flex-column d-flex"
                                                style="min-height: 300px"></uv-editor>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                                <v-card-actions class="d-flex justify-end">
                                    <uv-button :loading="userData.busy" color="success" type="submit">{{
                                        $t('save')
                                    }}</uv-button>
                                </v-card-actions>
                            </v-form>
                        </validation-observer>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6" align-self="start">
                    <v-card class="fill-height">
                        <v-card-title>
                            <span>
                                {{ $t('labels.changePassword') }}
                            </span>
                        </v-card-title>
                        <v-card-text class="px-0 pb-0">
                            <change-password-form ref="pwdForm"></change-password-form>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-sheet>
    </v-container>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import ChangePasswordForm from "@/views/Profile/ChangePassword";
import { mdiPhone, mdiPencil, mdiAccount } from "@mdi/js";

export default {
    name: "UserProfile",
    components: {
        ChangePasswordForm
    },
    data() {
        return {
            icons: { mdiPhone, mdiPencil, mdiAccount },
            userData: {
                data: {},
                busy: false,
            },
            avatarData: {
                image: null,
                busy: false,
            },
            currentAvatarData: {
                image: new Image(),
                busy: false,
            }
        }
    },
    created() {
        this.getUserData()
    },
    computed: {
        ...mapState(["currentUser"]),
    },
    methods: {
        ...mapMutations({
            setCurrentUser: "SET_currentUser",
            setGlobalMessage: "SET_globalMessage",
        }),
        getUserData() {
            return new Promise((resolve, reject) => {
                this.userData.busy = true;

                this.$apiHttp({
                    url: "/users/me"
                }).then((res) => {
                    this.setCurrentUser(res.data.data);
                    resolve(res.data.data);
                }).catch(error => {
                    reject(error);
                })
                    .finally(() => {
                        this.userData.busy = false
                    })
            })
        },
        updateUserDetails() {
            this.userData.busy = true;
            
            this.$apiHttp({
                url: "/users/me",
                method: "PUT",
                data: {...this.userData.data, text: this.$refs.signature.editor.getHTML()},
            }).then(() => {
                this.getUserData()
                this.$refs.observer.reset();
            }).finally(() => {
                this.userData.busy = false;
            })
        },
        onImageLoad(file) {
            if (file) {
                this.avatarData.image = file;
                this.uploadAvatar();
            }
        },
        uploadAvatar() {
            this.avatarData.busy = true;

            let form = new FormData();
            form.append("avatar", this.avatarData.image);

            this.$apiHttp({
                method: "put",
                url: '/users/me/avatar',
                data: form
            }, null, {}).then(() => {
                this.getUserData()
            }).finally(() => this.busy = false)
        },
        getImage() {
            if (this.userData.data.photo) {
                this.currentAvatarData.busy = true;

                this.$apiHttp({
                    url: "/get_file",
                    responseType: "blob",
                    params: {
                        path: this.userData.data.photo
                    }
                }, true).then((res) => {
                    this.currentAvatarData.image.src = URL.createObjectURL(res.data);
                }).catch(() => {
                    this.userData.data.photo = null;
                    this.setGlobalMessage({ message: this.$t('messages.avatarNotFound'), severity: "error" });
                }).finally(() => this.currentAvatarData.busy = false);
            }
        },
    },
    watch: {
        currentUser: {
            handler(newValue) {
                this.userData.data = newValue;
                if (newValue.text){
                    this.$refs.signature?.editor.commands.setContent(newValue.text)
                }
                this.getImage()
            },
            immediate: true
        },
    }
}
</script>

<style lang="scss" scoped>
.overflow-container {
    overflow-y: auto;

    &::-webkit-scrollbar-track {
        background: transparent;
        margin-block: 20px;
    }
}

.edit-avatar {
    position: absolute;
    right: 5%;
    bottom: 5%;
}
</style>